import type {
  ShareLeadResponse,
  ShareLeadUsage,
  SharePlaybackResponse,
  ShareUrlResponse,
} from "@/models/shareLead";
import { API_BASE, type APIResponse, callApi } from "@/services/api";
import { generateHMAC } from "@/services/hmac";

export async function createShareLead(opts: {
  email: string;
  referralCode?: string | null;
  videoId?: number | null;
  playbackIds?: string[];
  marketingOptIn?: boolean;
}): Promise<ShareLeadResponse> {
  const hmac = await generateHMAC();
  const body = {
    email: opts.email,
    referralCode: opts.referralCode,
    videoId: opts.videoId,
    playbackIds: opts.playbackIds,
    marketingOptIn: opts.marketingOptIn,
  };

  const resp = (await callApi(`${API_BASE}/share_leads`, {
    method: "POST",
    headers: hmac,
    body,
  })) as APIResponse<ShareLeadResponse>;

  return resp as unknown as ShareLeadResponse;
}

export async function shareLeadPlaybackTokens(opts: {
  leadToken?: string | null;
  leadId?: number | null;
  email?: string | null;
  playbackIds: string[];
  videoId?: number | null;
}): Promise<SharePlaybackResponse> {
  const hmac = await generateHMAC();
  const body = {
    leadToken: opts.leadToken,
    leadId: opts.leadId,
    email: opts.email,
    playbackIds: opts.playbackIds,
    videoId: opts.videoId,
  };

  const resp = (await callApi(`${API_BASE}/share_leads/playback_tokens`, {
    method: "POST",
    headers: hmac,
    body,
  })) as APIResponse<SharePlaybackResponse>;

  return resp as unknown as SharePlaybackResponse;
}

export async function shareLeadUsage(opts: {
  leadToken?: string | null;
  leadId?: number | null;
  email?: string | null;
}): Promise<ShareLeadUsage> {
  const hmac = await generateHMAC();
  if (opts.leadToken) {
    const resp = (await callApi(
      `${API_BASE}/share_leads/me?lead_token=${encodeURIComponent(opts.leadToken)}`,
      { headers: hmac },
    )) as APIResponse<ShareLeadUsage>;
    return resp as unknown as ShareLeadUsage;
  }

  const params = new URLSearchParams();
  if (opts.email) params.append("email", opts.email);
  if (opts.leadId) params.append("lead_id", String(opts.leadId));
  const resp = (await callApi(
    `${API_BASE}/share_leads/${opts.leadId || "me"}/usage?${params.toString()}`,
    { headers: hmac },
  )) as APIResponse<ShareLeadUsage>;
  return resp as unknown as ShareLeadUsage;
}

// callApi's success path merges the parsed response body directly onto the
// returned object rather than nesting it under `.data` (see api.ts), and the
// API is observed to send this field in either camelCase or snake_case
// depending on endpoint version, hence checking both below.
interface ShareLinkPayload {
  shareUrl?: string;
  share_url?: string;
}

export async function getShareUrl(opts: {
  resourceType: "routine" | "path" | "pose" | "home" | "explore" | "discover";
  resourceId?: number | string;
  slug?: string;
  title?: string;
  description?: string;
  thumbnailUrl?: string;
}): Promise<ShareUrlResponse> {
  const resp = (await callApi(`${API_BASE}/share_links`, {
    method: "POST",
    body: {
      resourceType: opts.resourceType,
      resourceId: opts.resourceId,
      title: opts.title,
      description: opts.description,
      imageUrl: opts.thumbnailUrl,
    },
  })) as APIResponse<ShareLinkPayload> & Partial<ShareLinkPayload>;

  const data = resp.data || resp;
  return { url: data.shareUrl || data.share_url } as ShareUrlResponse;
}
